<template>
    <!-- Product Buttons -->
    <div class="order">

        <!-- Loading Overlay -->
        <v-overlay :value="busy" :z-index="999999">
            <v-progress-circular indeterminate size="44" />
        </v-overlay>

        <v-tabs-items
            v-model="currentType"
            class="tabs-items"
            touchless
        >
            <v-tab-item
                v-for="(type, index) in appConfig.product.types"
                :key="index"
                class="pa-1 no-transition"
                :value="`${type}`"
                :transition="false"
            >
                <product-type-buttons
                    :ref="`${type}ProductTypeButtons`"
                    :type="type"
                    :active-type="currentType"
                    @select="selectProduct"
                />
            </v-tab-item>
        </v-tabs-items>

        <v-slide-y-reverse-transition hide-on-leave>
            <v-alert
                v-if="order && show.newItemsAlert"
                class="new-items-alert"
                color="#292c38"
                dark
                icon="mdi-firework"
                dense
                dismissible
                transition="slide-y-reverse-transition"
            >
                <div
                    ref="alertContent"
                    class="alert-content"
                    @click="eventBus.$emit('order-toggle-drawer')"
                >
                    <div v-for="(chunk, index) in newItemsChunks" :key="`alert-item-chunk-${index}`">
                        <div v-for="item in chunk" :key="`alert-item-${item.hash}`" class="alert-item-chunk">
                            {{ item.getCount() }} x {{ item.name }}
                        </div>
                    </div>
                </div>
            </v-alert>
        </v-slide-y-reverse-transition>

        <!-- Cash Dialog -->
        <numpad-expanded
            :show.sync="show.cash"
            type="cash"
            :persist="true"
            :title="$t('general.cash')"
            :value="order ? summary.toPay.toRoundedUnit(2) : 0"
            :total="order ? summary.toPay.toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'cash',
                value: $event.value,
                flag: !$event.addToDebt ? 'debt,skip' : null
            })"
        />

        <!-- Credit Card Dialog -->
        <numpad-expanded
            :show.sync="show.creditCard"
            type="creditCard"
            :title="`${$t('general.credit_card')} / ${$t('general.cash')}`"
            :value="0"
            :total="summary.toPay.toRoundedUnit(2)"
            :max="summary.toPay.toRoundedUnit(2)"
            @confirm="initOrderAction({
                paymentType: 'credit_card',
                value: $event.value > summary.toPay.toRoundedUnit(2) ? $event.value : $event.calculated
            })"
        />

        <!-- Advance Dialog -->
        <numpad-expanded
            :show.sync="show.advance"
            type="advance"
            :title="$t('general.advance')"
            :value="summary.toPay.toRoundedUnit(2)"
            :total="summary.toPay.toRoundedUnit(2)"
            :max="summary.toPay.toRoundedUnit(2)"
            @confirm="initOrderAction({
                paymentType: 'advance',
                value: $event.payments,
                flag: $event.paidDate ? 'advancePaidDate,' + $event.paidDate : null
            })"
        />

        <!-- Other NonCash Dialog -->
        <numpad-expanded
            :show.sync="show.otherNoncash"
            type="otherNoncash"
            :title="$t('general.other_noncash')"
            :value="0"
            :total="summary.toPay.toRoundedUnit(2)"
            :max="summary.toPay.toRoundedUnit(2)"
            @confirm="initOrderAction({
                paymentType: 'other_noncash',
                value: $event.value
            })"
        />

        <!-- Mixed Payments Dialog -->
        <numpad-expanded
            :show.sync="show.mixedPayment"
            type="mixedPayment"
            :title="$t('general.mixed')"
            :value="0"
            :total="summary.toPay.toRoundedUnit(2)"
            @confirm="initOrderAction({
                paymentType: 'mixed_payment',
                value: $event.payments
            })"
        />

        <!-- Discount Dialog -->
        <numpad-expanded
            :show.sync="show.discount"
            type="discount"
            :title="$t('order.discount')"
            :value="0"
            :total="order ? order.getPrice().toRoundedUnit(2) : 0"
            :max="99"
            @confirm="giveDiscount($event.value)"
        />

        <!-- Select Products Dialog -->
        <select-products
            :show.sync="show.selectProducts"
            @select="selectProducts($event)"
        />

        <!-- Select Client Dialog -->
        <select-client
            :show.sync="show.selectClient"
            :client-id="order ? order.client_id : null"
            @select="selectClient"
        />

        <!-- Pay By Invoice Dialog -->
        <invoice
            :show.sync="show.invoice"
            :order="order"
            mode="default"
            :preview="!unpaidOrders.length"
            @pay-order="initOrderAction({
                paymentType: $event.paymentType,
                registerType: einvoiceOnly ? 'manual' : null,
                ...$event
            })"
        />

        <!-- Proforma Invoice Dialog -->
        <invoice
            :show.sync="show.invoiceProforma"
            :order="order"
            mode="proforma"
            :preview="true"
            @save-order="saveAsProforma"
        />

        <!-- Pay By Einvoice Dialog -->
        <invoice
            :show.sync="show.einvoice"
            :order="order"
            mode="default"
            :is-einvoice="true"
            :preview="true"
            @pay-order="initOrderAction({
                paymentType: $event.paymentType,
                registerType: 'einvoice',
                ...$event
            })"
        />

        <!-- Other Payment Methods Dialog -->
        <payment-methods
            :show.sync="show.paymentMethods"
            @toggle-dialog="toggleDialog"
            @pay="initOrderAction({ paymentType: $event.paymentType, flag: $event.flag })"
        />

        <!-- Receipt Note -->
        <note
            :show.sync="show.note"
            :note="order ? order.note : null"
            @save="setNote($event.note)"
        />

        <!-- Add Reference Dialog -->
        <order-reference
            :show.sync="show.orderReference"
            @save="setCustomReference($event.refOrder, $event.refTime)"
        />

        <!-- Ota Sync SendToRoom Dialog -->
        <ota-sync
            :show.sync="show.sendToRoom"
            @save="onOrderActionFinish"
        />
    </div>
</template>

<script>
import NumpadExpanded from '@/components/NumpadExpanded';

import Invoice from '@/views/shared/components/Invoice';
import PaymentMethods from '@/views/shared/components/PaymentMethods';
import OrderReference from '@/views/shared/components/OrderReference';
import SelectClient from '@/views/shared/components/SelectClient';
import Note from '@/views/shared/components/Note';
import ProductTypeButtons from '@/views/aria/order/ProductTypeButtons';
import SelectProducts from '@/views/allegra/order/SelectProducts';
import OtaSync from '@/views/shared/components/integrations/OtaSync';

import Grid from '@/models/Grid';
import Button from '@/models/Button';
import Product from '@/models/Product';
import Table from '@/models/Table';
import Order from '@/models/Order';
import Client from '@/models/Client';
import Shift from '@/models/Shift';
import CashRegister from '@/models/CashRegister';
import Pricelist from '@/models/Pricelist';

import ApiService from '@/services/ApiService';
import UserService from '@/services/UserService';
import LogService from '@/services/LogService';
import EinvoiceService from '@/services/EinvoiceService';
import ErrorReporter from '@/wrappers/ErrorReporter';
import CardReadService from '@/wrappers/CardReadService';

import * as mutationTypes from '@/store/mutationTypes';
import { mapGetters } from 'vuex';

export default {
    name: 'Order',

    components: {
        NumpadExpanded,
        SelectClient,
        Note,
        ProductTypeButtons,
        Invoice,
        PaymentMethods,
        SelectProducts,
        OrderReference,
        OtaSync
    },

    created() {
        this.currentType = this.appConfig.product.types[0];

        this.eventBus.$on('notification-received', this.onNotificationReceived);

        // Events coming from app bar
        this.eventBus.$on('order-show-tab', this.showTab)

        // Events coming from the navigation drawer
        this.eventBus.$on('order-delete-item', this.deleteItem)
        this.eventBus.$on('order-select-item-comments', this.selectItemComments)
        this.eventBus.$on('order-count-update', this.updateItemCount)
        this.eventBus.$on('order-set-item-separator', this.setItemSeparator)
        this.eventBus.$on('order-discount', this.giveItemDiscount)
        this.eventBus.$on('order-drawer', this.updateDrawer)
        this.eventBus.$on('order-transfer-to-guest', this.transferToGuest)

        // Events coming from the bottom navigation
        this.eventBus.$on('order-toggle-dialog', this.toggleDialog);
        this.eventBus.$on('order-init-action', this.initOrderAction);
        this.eventBus.$on('order-cancel-order', this.cancelOrder);

        this.eventBus.$on('order-go-back', this.goBack);

        this.eventBus.$on('orders-updated', this.setUnpaidOrders)
        this.eventBus.$on('card-read-completed', this.onReadCardCompleted);

        // activated
        this.drawer = this.$vuetify.breakpoint.mdAndUp;

        // reset new items alert
        this.show.newItemsAlert = false;

        // if shift is not opened go back
        if (!this.curShift || this.curCashRegister.depositRequired()) {
            this.goBack();
            return;
        }

        const refersToAdvance = this.curOrder && this.curOrder.refersToAdvance() ? this.curOrder : null

        this.$store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: null });
        this.$store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: null });
        this.$store.commit(mutationTypes.SET_CUR_TABLE, { curTable: null });

        let tableId = this.$route.params.table_id;

        setTimeout(() => {
            const table = Table.find(tableId)
            this.table = table ? table.$duplicate() : null;
            this.createNewOrder(refersToAdvance);

            if(this.table) {
                this.setUnpaidOrders();

                // if not allowed to access this table
                if(!this.canAccessTable(this.table, this.allUnpaidOrders)) {
                    this.goBack();
                    return;
                }
            }

            this.loadData(tableId)
        }, 1)
    },

    beforeDestroy(){
        this.eventBus.$off('notification-received', this.onNotificationReceived);

        // Events coming from app bar
        this.eventBus.$off('order-show-tab', this.showTab)

        // Events coming from the navigation drawer
        this.eventBus.$off('order-delete-item', this.deleteItem)
        this.eventBus.$off('order-select-item-comments', this.selectItemComments)
        this.eventBus.$off('order-set-item-separator', this.setItemSeparator)
        this.eventBus.$off('order-count-update', this.updateItemCount)
        this.eventBus.$off('order-discount', this.giveItemDiscount)
        this.eventBus.$off('order-drawer', this.updateDrawer)
        this.eventBus.$off('order-transfer-to-guest', this.transferToGuest)

        // Events coming from the bottom navigation
        this.eventBus.$off('order-toggle-dialog', this.toggleDialog);
        this.eventBus.$off('order-init-action', this.initOrderAction);
        this.eventBus.$off('order-cancel-order', this.cancelOrder);

        this.eventBus.$off('order-go-back', this.goBack);

        this.eventBus.$off('orders-updated', this.setUnpaidOrders)
        this.eventBus.$off('card-read-completed', this.onReadCardCompleted)

        this.$store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: null });
        this.$store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: null });
        this.$store.commit(mutationTypes.SET_CUR_TABLE, { curTable: null });
    },

    data: () => ({
        currentType: null,

        drawer: false,
        busy: false,

        order: null,
        allUnpaidOrders: [],
        table: null,

        show: {
            cash: false,
            creditCard: false,
            otherNoncash: false,
            mixedPayment: false,
            invoice: false,
            einvoice: false,
            advance: false,
            paymentMethods: false,
            invoiceProforma: false,
            discount: false,
            count: false,
            selectClient: false,
            newItemsAlert: false,
            selectProducts: false,
            orderReference: false,
            note: false,
            sendToRoom: false,
        },
    }),

    watch: {
        curShift(val) {
            if (!val) {
                this.goBack();
            }
        },

        splitReceipt: {
            deep: true,
            handler(val) {
                if(val.active) {
                    this.order.items = val.data.flatMap(sr => sr.items).map(i => {
                        const newItem = i.item.fresh(this.order)
                        newItem.count = i.count
                        return newItem
                    })
                } else {
                    this.createNewOrder();
                }
            }
        },

        activePricelist(val) {
            if(this.order) {
                this.order.setPricelist(val)
            }

            if(locale !== 'me' && !this.splitReceipt.active) {
                this.unpaidOrders.forEach(order => order.setPricelist(val, true))
            }
        },

        drawer(val) {
            this.show.newItemsAlert = !val && this.order && this.order.items.length > 0;
        },

        order: {
            deep: true,
            handler(val) {
                this.$store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: val });
            }
        },

        allUnpaidOrders: {
            deep: true,
            handler(val) {
                this.$store.commit(mutationTypes.SET_CUR_UNPAID_ORDERS, { curUnpaidOrders: val });
            }
        },

        table: {
            deep: true,
            handler(val) {
                this.$store.commit(mutationTypes.SET_CUR_TABLE, { curTable: val });
            }
        },

        curGuest(val) {
            this.order.setGuestId(val);
            this.$nextTick(() => this.selectClient(this.existingClient, true))
        }
    },

    computed: {
        ...mapGetters([
            'curOrder',
            'curTable',
            'curGuest',
            'unpaidOrders',
            'tablePermissions',
            'splitReceipt',
            'activePricelist',
            'pendingCardPayment',
        ]),

        curShift() {
            return Shift.current();
        },

        curCashRegister() {
            return CashRegister.current();
        },

        newItemsChunks() {
            if (this.order.items.length < 2) {
                return [this.order.items];
            }

            return _.chunk(this.order.items, 2);
        },

        summary() {
            if(this.splitReceipt.active) {
                return {
                    price: this.order.getPrice(),
                    discount: this.order.getDiscountAmount(),
                    toPay: this.order.getLeftToPay()
                }
            }

            let price = Money.parse(0)
            let discount = Money.parse(0)
            let toPay = Money.parse(0)

            this.unpaidOrders.forEach(order => {
                price = price.add(order.getPrice())
                discount = discount.add(order.getDiscountAmount())
                toPay = toPay.add(order.getToPay())
            })

            if(this.order) {
                price = price.add(this.order.getPrice())
                discount = discount.add(this.order.getDiscountAmount())
                toPay = toPay.add(this.order.getLeftToPay())
            }

            return {
                price: price,
                discount: discount,
                toPay: toPay,
            }
        },

        existingClient() {
            if(!this.table) return null;
            const unpaidOrderWithClient = this.unpaidOrders.find(order => !!order.client_id);
            return unpaidOrderWithClient?.getClient() || null;
        },

        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        },

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        }
    },

    methods: {
        loadData(tableId = null) {
            if (!this.isOnline) return;

            const params = {}

            if(tableId) {
                params.table = tableId
            }

            const productsLastUpdatedAt = Product.getLastUpdatedAt();
            params.products_last_updated_at = productsLastUpdatedAt ? productsLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null

            this.busy = true;

            ApiService.get('/load/orders', { params: params })
                .then(response => {
                    this.busy = false;

                    this.table = response.data.table ? new Table(response.data.table) : null

                    if (response.data.orders) {
                        this.allUnpaidOrders = response.data.orders.map(order => new Order(order));
                    }

                    Order.insert({ data: response.data.orders, freeze: true });
                    Product.insert({ data: response.data.products, freeze: true });

                    // Shift.insert({ data: [response.data.shift] });
                    // Grid.insert({ data: response.data.grids });
                    // Button.insert({ data: response.data.buttons });
                    // Client.insert({ data: response.data.clients });
                }).catch(error => {
                    this.busy = false;
                    console.log(error.message)
                });
        },

        setUnpaidOrders() {
            this.allUnpaidOrders = Order.onTables()
                .filter(o => o.table_id == this.table?.id)
                .map(order => order.$duplicate())
        },

        selectProduct(product, quantity = 1) {
            if(!this.order) {
                this.createNewOrder();
            }

            const selectedItem = this.order.addItem(product, quantity);
            if(!selectedItem) return;

            if (!this.drawer) {
                this.show.newItemsAlert = true;
            }

            this.eventBus.$emit('order-select-item', selectedItem)
        },

        /**
         * Fire issue or pay order action
         */
        async initOrderAction(options = {}) {
            const { paymentType, value } = options

            if(this.noncashOnly && !['invoice', 'credit_card_business', 'order'].includes(paymentType)) return

            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            if(paymentType === 'advance' && !this.order.client_id) {
                this.show.selectClient = true;
                this.$snackbar(this.$t('order.buyer_select'), 'error');
                return;
            }

            if(this.order) {
                const hasItemsWithNegativeCount = this.order.items.some(item => item.getCount() < 0);
                if(hasItemsWithNegativeCount && (!this.order.flag || !this.order.flag.startsWith('ref,'))) {
                    this.show.orderReference = true;
                    return;
                }
            }

            if(paymentType !== 'order' && !this.curCashRegister.allowsOrderPayment(paymentType, value)) {
                this.$snackbar(this.$t('order.error.payment_type_not_allowed'), 'error');
                return;
            }

            const unpaidOrders = !this.splitReceipt.active ? [...this.unpaidOrders] : []
            if(CardReadService.shouldReadCard(this.order, unpaidOrders, options)) {
                this.readCard(options)
                return
            }

            await this.executePay(options)
        },

        async executePay(options) {
            const { paymentType } = options

            this.busy = true

            try {
                if(paymentType === 'order') {
                    await this.issueOrder()
                    this.busy = false
                    return
                }

                if(this.unpaidOrders.length && !this.splitReceipt.active) {
                    await this.payAllOrders(options)
                } else {
                    await this.payOrder(options)
                }
            } catch(error) {
                console.log(error.message)

                this.$snackbar(this.$t('general.error_contact_support'), 'error', {
                    mode: 'multi-line',
                    timeout: 10000,
                    title: this.$t('order.error.title')
                })

                const orderToLog = this.order ? this.order.$duplicate() : null
                if(orderToLog) {
                    orderToLog.items = JSON.stringify(orderToLog.items)
                    orderToLog.taxes = JSON.stringify(orderToLog.taxes)
                }

                ErrorReporter.reportMessage('Order action exception', {
                    error: error.message,
                    order: orderToLog,
                })

                this.$router.push({ name: 'tables' }).catch(err => {});
            } finally {
                this.busy = false
            }
        },

        async readCard(options) {
            this.$confirm(
                this.$t('order.card_read_warning'),
                {
                    buttonTrueText: this.$t('general.yes'),
                    buttonCancelText: this.$t('general.no'),
                    icon: null,
                    color: 'primary',
                    title: this.$t('general.credit_card'),
                    persistent: true,
                },
                async result => {
                    if(!result) {
                        await this.executePay(options)
                        return
                    }

                    const unpaidOrders = !this.splitReceipt.active ? [...this.unpaidOrders] : []
                    const startCardReadResult = await CardReadService.startCardRead(this.order, unpaidOrders, options)
                    if(startCardReadResult.success) return

                    this.$confirm(this.$t(`order.error.card_read.${startCardReadResult.error}`), {
                        icon: 'fal fa-exclamation-triangle',
                        color: 'error',
                        title: this.$t('general.action_failed')
                    }, async result => {});
                }
            )
        },

        async onReadCardCompleted(result) {
            if(result.success && this.pendingCardPayment && this.pendingCardPayment.paymentSessionToken === result.paymentSessionToken) {
                this.order = this.pendingCardPayment.order

                let flag = this.pendingCardPayment.options.flag
                const options = { ...this.pendingCardPayment.options, flag: flag ? flag + '|payment,card' : 'payment,card' }

                await this.executePay(options)
            } else {
                this.$confirm(this.$t(`order.error.card_read.${result.error}`), {
                    icon: 'fal fa-exclamation-triangle',
                    color: 'error',
                    title: this.$t('general.action_failed')
                }, async result => {});
            }
        },

        /**
         * Issue single order
         */
        async issueOrder(options = {}) {
            const result = await this.order.$duplicate().issue({
                user: this.user,
                choosePrinter: options.choosePrinter
            })

            this.onOrderIssue(result);
        },

        /**
         * Pay single order
         */
        async payOrder(options = {}) {
            const {
                paymentType,
                value,
                internalNumber,
                choosePrinter,
                flag,
                forceInvoiceForm,
                registerType = null
            } = options

            if(!this.order || !this.order.items.length) return

            this.order.internal_number = internalNumber || null

            const result = await this.order.$duplicate().pay({
                user: this.user,
                paymentType: paymentType,
                value: value,
                choosePrinter: choosePrinter,
                forceInvoiceForm: forceInvoiceForm,
                flag: flag,
                openDrawer: false,
                registerType: registerType
            })

            this.onOrderIssue(result);
        },

        /**
         * Pay all orders
         */
        async payAllOrders(options = {}) {
            const {
                paymentType,
                value,
                internalNumber,
                choosePrinter,
                flag,
                forceInvoiceForm,
                registerType = null
            } = options

            const orders = [...this.unpaidOrders]
            if(this.order && this.order.items.length) {
                const result = await this.order.$duplicate().issue({
                    user: this.user,
                })

                if(result.status === Order.SYNC_STATUS_FAILED) return

                orders.push(this.order)

                const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
                await delay(5000);
            }

            const result = await Order.payAll(orders, {
                user: this.user,
                table: this.table,
                client: this.order.client,
                internalNumber: internalNumber,
                paymentType: paymentType,
                value: value,
                choosePrinter: choosePrinter,
                forceInvoiceForm: forceInvoiceForm,
                flag: flag,
                openDrawer: false,
                dueDate: this.order.due_date,
                startDate: this.order.start_date,
                endDate: this.order.end_date,
                note: this.order.note,
                registerType: registerType
            })

            this.onOrderIssue(result);
        },

        /**
         * Save order as proforma
         */
        async saveAsProforma(options = {}) {
            const { internalNumber, choosePrinter, paymentType, forceInvoiceForm } = options

            this.order.internal_number = internalNumber || null
            const result = await this.order.$duplicate().proforma({
                user: this.user,
                paymentType: paymentType,
                choosePrinter: choosePrinter,
                forceInvoiceForm: forceInvoiceForm,
            });

            this.onOrderIssue(result);
        },

        /**
         * Cancel order
         */
        cancelOrder() {
            if (!this.allows('allow_cancel_order')) {
                return;
            }

            this.$snackbar(this.$t('order.canceled'));
            this.onOrderActionFinish();

            LogService.info({
                type: 'cancel_receipt',
                value: this.order?.getToPay().toFormat('0.00') || '0.00' }
            );
        },

        /**
         * On order issue
         */
        async onOrderIssue(result, successMsg = null) {
            if(!successMsg) {
                successMsg = this.order?.isEinvoice() ? this.$t('einvoice.issued') : this.$t('order.issued');
            }

            if(result.status === Order.SYNC_STATUS_FAILED) {
                this.$confirm(result.error, {
                    icon: 'fal fa-exclamation-triangle',
                    color: 'error',
                    title: this.$t('order.error.title'),
                }, async result => {});

                return
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS) {
                this.$snackbar(successMsg, 'success', { timeout: 3000 })
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS_WITH_ERRORS) {
                this.$confirm(result.error || result.efiError || result.printError, {
                    icon: 'fal fa-exclamation-circle',
                    color: 'warning',
                    title: successMsg,
                }, async result => {});
            }

            if(this.splitReceipt.active) {
                await this.deleteSplitItems()
            }

            if(this.activePricelist && result?.order?.isSale()) {
                const pricelist = Pricelist.find(this.activePricelist)
                LogService.info({
                    type: 'pricelist',
                    value: result?.order?.getToPay().toFormat('0.00') || '0.00',
                    pricelist: pricelist?.name
                });
            }

            this.onOrderActionFinish()
        },

        /**
         * On order action finish
         */
        onOrderActionFinish() {
            this.show.newItemsAlert = false;

            if (this.allows('nav_order_issued_return_to_tables')) {
                this.$router.push({ name: 'tables' }).catch(err => {});
            } else if (this.allows('nav_order_issued_logout')) {
                this.logout();
            } else if (this.allows('nav_order_issued_stay_on_order')) {
                this.createNewOrder();

                if (this.$route.params.order_hash) {
                    this.$router.replace({
                        name: 'order',
                        params: { table_id: this.table.id }
                    });
                }

                if(this.table) {
                    this.setUnpaidOrders()
                    this.loadData(this.table.id)
                }

                this.appConfig.product.types.forEach(type => {
                    const productTypeBtns = this.$refs[`${type}ProductTypeButtons`]
                    if(productTypeBtns && productTypeBtns.length) {
                        this.$nextTick(() => productTypeBtns[0].resetCounts())
                    }
                })
            }
        },

        async deleteSplitItems() {
            for(let i = 0; i < this.splitReceipt.data.length; i++) {
                const sr = this.splitReceipt.data[i];

                for(let j = 0; j < sr.items.length; j++) {
                    const srItem = sr.items[j];
                    const isLastItem = j === sr.items.length - 1;
                    const updateItemCountResult = await sr.order.updateItemCount(srItem.item, srItem.item.getCount() - parseFloat(srItem.count), isLastItem)
                    const updatedOrder = updateItemCountResult.order;

                    if(!updatedOrder) {
                        this.allUnpaidOrders = this.allUnpaidOrders.filter(o => o.hash !== sr.order.hash)
                    }
                }
            }

            this.eventBus.$emit('order-split-receipt-reset')
        },

        async transferToGuest(guestId) {
            this.busy = true;
            this.order.setGuestId(guestId)

            try {
                const result = await this.order.$duplicate().issue({ user: this.user, skipPrint: true })
                await this.onOrderIssue(result, this.$t('order.transfered'));
            } catch(error) {
                console.log(error.message)
            } finally {
                this.busy = false
            }
        },

        giveDiscount(value) {
            if (!this.allows('allow_discount')) {
                return;
            }

            const orders = locale === 'me' ? [this.order] : [...this.unpaidOrders, this.order]

            const uniqueDiscounts = orders.flatMap(o => o.items)
                .map(item => item.getDiscountPercent())
                .filter((val, index, array) => index === array.indexOf(val))

            if(uniqueDiscounts.length > 1) {
                this.$snackbar(this.$t('order.discount_not_possible'), 'error', { timeout: 10000 });
                return
            }

            this.order.giveDiscount(value)

            if(locale !== 'me' && !this.splitReceipt.active) {
                this.unpaidOrders.forEach(order => {
                    order.giveDiscount(value, true)
                })
            }
        },

        createNewOrder(order = null) {
            this.order = order || Order.createNew(this.user, this.table);
            this.order.setGuestId(this.curGuest);

            this.$nextTick(() => {
                const existingPricelist = this.unpaidOrders.length ? this.unpaidOrders[0].getPricelist() : 0
                if(existingPricelist) {
                    this.order.setPricelist(existingPricelist)
                }

                const client = this.order.getClient() || this.existingClient
                this.selectClient(client, true)
            })
        },

        selectClient(client, subsequentSelect = false) {
            const includeUnpaidOrders = locale !== 'me' && !this.splitReceipt.active;
            const orders = !includeUnpaidOrders ? [this.order] : [...this.unpaidOrders, this.order]
            const uniqueDiscounts = orders.flatMap(o => o.items)
                .map(item => item.getDiscountPercent())
                .filter((val, index, array) => index === array.indexOf(val))

            const applyDiscount = uniqueDiscounts.length === 1;

            if(this.order) {
                this.order.setClient(client, applyDiscount);
            }

            if(includeUnpaidOrders) {
                this.allUnpaidOrders = this.allUnpaidOrders.map(order => {
                    const guestId = order.getGuestId() || 0;
                    if(guestId === this.curGuest) {
                        order.setClient(client, applyDiscount, true);
                    }
                    return order;
                });
            }

            if(client?.pricelist_id) {
                this.eventBus.$emit('pricelist-update', client.pricelist_id);
            }

            if(!this.order || subsequentSelect) return;

            const zeroTaxMode = this.order.export || !!client?.isDiplomat();
            if(zeroTaxMode) {
                let message = this.$t('order.zero_tax')
                const reverseCalculation = !!this.tenant.venue.settings.reverse_calculation
                if(!reverseCalculation) {
                    message = this.$t('order.zero_tax_prices') + ' ' + message
                }
                this.$snackbar(message, 'warning', { timeout: 10000 });
            }
        },

        setNote(note) {
            this.order.setNote(note);
        },

        selectItemComments(data) {
            this.order.setItemComments(data.item, data.comments);
        },

        async deleteItem(data) {
            LogService.info({
                type: 'cancel_item',
                value: data?.item?.getToPay().toFormat('0.00') || '0.00'
            });

            const deleteItemResult = await data.order.deleteItem(data.item, !data.order.isNew());
            const updatedOrder = deleteItemResult.order;

            if(data.order.hash === this.order.hash) {
                this.order = updatedOrder
                if(!this.order) {
                    this.createNewOrder();
                }

                this.appConfig.product.types.forEach(type => {
                    const productTypeBtns = this.$refs[`${type}ProductTypeButtons`]
                    if(productTypeBtns && productTypeBtns.length) {
                        this.$nextTick(() => productTypeBtns[0].calculateCounts(data.item.product_id))
                    }
                })

                return
            }

            if(!updatedOrder) {
                this.allUnpaidOrders = this.allUnpaidOrders.filter(order => order.hash !== data.order.hash)
            }
        },

        updateItemCount(data) {
            this.order.updateItemCount(data.item, data.count);
        },

        giveItemDiscount(data) {
            if (!this.allows('allow_item_discount')) {
                return;
            }

            data.order.giveItemDiscount(data.item, data.percent, !data.order.isNew());
        },

        setItemSeparator() {
            this.order.setItemSeparator();
        },

        setCustomReference(refOrder, refTime) {
            this.order.setCustomReference(refOrder, refTime);
        },

        canAccessTable(table, orders) {
            if(!orders.length) return true;

            if(this.allows('allow_enter_all_tables')) return true;

            const curUser = new User(this.user);
            return curUser.getTables().some(t => t.id === table.id);
        },

        goBack() {
            this.show.newItemsAlert = false;

            if (this.allows('nav_order_back_return_to_tables')) {
                this.$router.push({ name: 'tables' }).catch(err => {});
            } else if (this.allows('nav_order_back_logout')) {
                this.logout();
            }
        },

        logout() {
            UserService.logout();
            this.$router.push({ name: 'userLogin' }).catch(err => {});
        },

        onNotificationReceived(notification) {
            if (!this.isOnline) {
                return;
            }

            if (notification.type == 'delete-order-item') {
                this.$snackbar(this.$t('order.another_client_updated_order'), 'warning');
                this.goBack();
            }
        },

        selectProducts(selected) {
            selected.forEach(item => {
                this.selectProduct(item.product, item.quantity)
            });
        },

        showTab(tab) {
            this.currentType = tab
        },

        toggleDialog(data) {
            this.show[data.dialog] = !this.show[data.dialog]
        },

        updateDrawer(drawer) {
            this.drawer = drawer
        }
    }
};
</script>

<style lang="scss" scoped>
.order {
    height: 100%;
    width: 100%;

    .tabs-items {
        height: 100%;
        width: 100%;

        ::v-deep .v-window__container {
            height: 100%;

            .v-window-item {
                height: 100%;
            }
        }

        .tab-container {
            height: 100%;
        }
    }

    .new-items-alert {
        position: fixed;
        border-radius: 0;
        left: 0;
        right: 0;
        bottom: 60px;
        font-size: 12px;
        padding: 0;

        @media screen and (min-width: 600px) {
            font-size: 14px;
        }

        ::v-deep .v-alert__icon {
            display: none;
        }

        ::v-deep .v-btn--icon {
            color: #ffffff;
            background: #292c38;
            position: absolute;
            right: 20px;
            top: 2px;
        }

        .alert-content {
            display: flex;
            flex-wrap: wrap;
            padding: 8px 10px;
            cursor: pointer;

            .alert-item-chunk {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                width: 90px;
                margin-right: 10px;

                @media screen and (min-width: 600px) {
                    width: inherit;
                    min-width: 150px;
                }
            }
        }
    }
}
</style>
